import { AuthGateway } from './gateway/auth.gateway';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, take, tap, switchMap, filter, of } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { AuthState } from '../state/auth/auth.state';
import { Store } from '@ngrx/store';
import { login, logout } from '../state/auth/auth.actions';
import { selectToken } from '../state/auth/auth.selectors';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private authGateway: AuthGateway, private store: Store<AuthState>) {}

  public authenticate(email: string, password: string): Observable<any> {
    return this.authGateway.authenticate(email, password).pipe(
      tap((response) => {
        const token = response?.token;
        this.registerLogin(token);
      })
    );
  }

  public register(registerData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    opapCode: string;
    companyName: string;
    afm: string;
    verifiedCode: string;
  }): Observable<any> {
    return this.authGateway.register(registerData).pipe(
      switchMap((response) => {
        const token = response?.token;
        if (!token) {
          throw new Error('No token received from registration');
        }
        // Save token first
        this.registerLogin(token);
        // Wait for token to be stored in the store before continuing
        return this.store.select(selectToken).pipe(
          filter((storedToken) => storedToken === token && !!storedToken),
          take(1),
          switchMap(() => {
            // Return the response after token is confirmed in store
            return of(response);
          })
        );
      })
    );
  }

  // Method to log in the user and set the token
  private registerLogin(token: string): void {
    if (!token) {
      console.error('No token provided to registerLogin');
      return;
    }
    // Update tokenSubject first
    this.tokenSubject.next(token);
    // Dispatch login action to store
    this.store.dispatch(login({ token: token, user: null }));
  }

  // Logout method to clear token from memory and storage
  public logout(): void {
    this.tokenSubject.next(null);
    this.store.dispatch(logout());
  }

  // Observable for components to watch token changes
  public getTokenObservable(): Observable<string | null> {
    return this.tokenSubject.asObservable();
  }

  // Add isAuthenticated method
  public isAuthenticated(): boolean {
    // Check if a token exists and is not expired
    let token: string | null = null;

    // Synchronously get the token from the BehaviorSubject or the store
    this.store
      .select(selectToken)
      .pipe(take(1))
      .subscribe((storedToken) => {
        token = storedToken;
      });

    return !!token && !this.isTokenExpired(token);
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1])); // Decode the JWT payload
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      return Date.now() > expiryTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true; // Treat as expired if decoding fails
    }
  }
}
